/**
 * PricingSection - Pricing cards for Free and Pro plans
 *
 * Shows explicit limits to set correct expectations:
 * - Free: 1 preset/month, 3 iterations, no credit card
 * - Pro: $10/month, higher limits
 */

"use client";

import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { ScrollReveal } from "./effects";
import GradientText from "@/components/ui/GradientText";
import GradientButton from "@/components/ui/GradientButton";
import { Check } from "lucide-react";
import { isRegistrationEnabled } from "@/lib/feature-flags";

interface PricingSectionProps {
  isAuthenticated: boolean;
}

export default function PricingSection({
  isAuthenticated,
}: PricingSectionProps) {
  const t = useTranslations("landing.pricing");

  return (
    <section className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
          <GradientText>{t("title")}</GradientText>
        </h2>
        <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
          {t("subtitle")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Plan */}
          <ScrollReveal delay={0}>
            <div
              className="
                relative overflow-hidden rounded-2xl p-8 h-full flex flex-col
                bg-white/5 backdrop-blur-xl border border-gray-700/50
                shadow-[0_0_25px_-10px_rgba(156,163,175,0.3)]
                hover:shadow-[0_0_35px_-10px_rgba(156,163,175,0.5)]
                hover:border-gray-600/50 hover:-translate-y-1
                transition-all duration-300
              "
            >
              {/* Subtle gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-500/5 via-transparent to-transparent" />
              {/* Top highlight */}
              <div className="absolute top-0 left-6 right-6 h-[1px] bg-gradient-to-r from-transparent via-gray-400/15 to-transparent" />

              <div className="relative mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">
                  {t("free.name")}
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">$0</span>
                  <span className="text-gray-400">{t("perMonth")}</span>
                </div>
              </div>

              <ul className="relative space-y-3 mb-8 flex-grow">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">{t("free.presets")}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">{t("free.iterations")}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">{t("free.noCard")}</span>
                </li>
              </ul>

              <Link
                href={isAuthenticated ? "/build-preset" : isRegistrationEnabled ? "/register" : "/login"}
                className="relative block"
              >
                <button className="w-full h-12 px-6 font-semibold text-white border border-gray-600 rounded-lg hover:border-gray-500 hover:bg-white/5 transition-all duration-300">
                  {t("free.cta")}
                </button>
              </Link>
            </div>
          </ScrollReveal>

          {/* Pro Plan */}
          <ScrollReveal delay={0.15}>
            <div
              className="
                relative overflow-hidden rounded-2xl p-8 h-full flex flex-col
                bg-gradient-to-br from-pink-500/10 to-purple-500/10 backdrop-blur-xl
                border border-pink-500/30
                shadow-[0_0_30px_-10px_rgba(236,72,153,0.4)]
                hover:shadow-[0_0_40px_-10px_rgba(236,72,153,0.6)]
                hover:border-pink-500/50 hover:-translate-y-1
                transition-all duration-300
              "
            >
              {/* Subtle gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/8 via-transparent to-transparent" />
              {/* Top highlight */}
              <div className="absolute top-0 left-6 right-6 h-[1px] bg-gradient-to-r from-transparent via-pink-400/20 to-transparent" />

              {/* Popular badge */}
              <div className="absolute top-4 right-4">
                <span className="bg-gradient-to-r from-pink-600 to-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {t("pro.badge")}
                </span>
              </div>

              <div className="relative mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">
                  {t("pro.name")}
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">$10</span>
                  <span className="text-gray-400">{t("perMonth")}</span>
                </div>
              </div>

              <ul className="relative space-y-3 mb-8 flex-grow">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-pink-400 flex-shrink-0" />
                  <span className="text-gray-200">{t("pro.presets")}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-pink-400 flex-shrink-0" />
                  <span className="text-gray-200">{t("pro.iterations")}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-pink-400 flex-shrink-0" />
                  <span className="text-gray-200">{t("pro.regularUse")}</span>
                </li>
              </ul>

              <Link
                href={isAuthenticated ? "/profile/subscription" : isRegistrationEnabled ? "/register" : "/login"}
                className="relative block"
              >
                <GradientButton className="w-full" size="md" glow>
                  {t("pro.cta")}
                </GradientButton>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
